import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";

import { Dialog } from "primereact/dialog";
import MeaButton from "../../../../components/MeaButton";
import { Toast } from "primereact/toast";
import { ListBox } from "primereact/listbox";
import { Avatar } from "primereact/avatar";
import membersAPI from "../../../../services/members";
import teachersAPI from "../../../../services/teachers";
import { useParams } from "react-router-dom";

const UpdateTeacherMemberModal = forwardRef(({ teacher }, ref) => {
  const params = useParams();
  const schoolId = params.schoolId;
  const trainingId = params.trainingId;
  const accessToken = "";

  const [selectedMember, setSelectedMember] = useState(null);
  const [active, setActive] = useState(false);
  const [callbackFn, setCallbackFn] = useState(() => {});
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);

  // Load all school members with type: teacher
  const membersList = membersAPI.useGetList(
    schoolId,
    accessToken,
    1,
    10000,
    { type: "teacher" }
  );
  const uniqueMembers = membersList.data?.data || [];
  
  // Sort members alphabetically by display name
  uniqueMembers.sort((a, b) => {
    const nameA = (a.name || a.lastname + " " + a.firstname || "").toLowerCase();
    const nameB = (b.name || b.lastname + " " + b.firstname || "").toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const members = uniqueMembers.map((member) => ({
    key: member?.id,
    label:
      member?.name !== ""
        ? member?.name
        : member?.lastname + " " + member.firstname,
    value: member?.id,
    avatar: member?.avatar,
    name: member?.name,
    firstname: member?.firstname,
    lastname: member?.lastname,
  }));

  // Load current teachers to filter out those already associated with other teachers in this training
  const teachersList = teachersAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    100000
  );
  const uniqueTeachers = teachersList.data?.data || [];
  
  // Filter selectable members:
  // A member is selectable if they are NOT associated with another teacher in this training.
  // We allow the member associated with the CURRENT teacher (so they can keep the current selection).
  const selectableMembers = members.filter((member) => {
    return !uniqueTeachers.some(
      (t) => t.id !== teacher?.id && t.member?.id === member.value
    );
  });

  useEffect(() => {
    if (active) {
      membersList.refetch();
      teachersList.refetch();
      if (teacher?.member?.id) {
        setSelectedMember(teacher.member.id);
      } else {
        setSelectedMember(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, teacher]);

  const handleUpdateSubmit = async () => {
    if (!selectedMember) {
      toast.current.show({
        severity: "warn",
        summary: "Attention",
        detail: "Veuillez sélectionner un membre.",
      });
      return;
    }

    setLoading(true);
    teachersAPI
      .UpdateOne(schoolId, trainingId, accessToken, teacher?.id, {
        member_id: selectedMember,
      })
      .then(() => {
        toast.current.show({
          severity: "success",
          summary: "Succès",
          detail: "Membre associé mis à jour!",
        });
        setActive(false);
        callbackFn();
      })
      .catch((error) => {
        toast.current.show({
          severity: "error",
          summary: "Erreur",
          detail: "Erreur lors de la modification!",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useImperativeHandle(ref, () => ({
    show: (callback) => {
      setCallbackFn(() => callback);
      setActive(true);
    },
  }));

  const footerContent = (
    <div>
      <MeaButton
        label="Annuler"
        icon="pi pi-times"
        disabled={loading}
        onClick={() => setActive(false)}
        className="p-button-text"
      />
      <MeaButton
        type="submit"
        loading={loading}
        label="Modifier"
        icon="pi pi-check"
        onClick={() => handleUpdateSubmit()}
      />
    </div>
  );

  const memberTemplate = (option) => {
    return (
      <div className="flex align-items-center">
        <Avatar
          className="mr-2 p-overlay-badge"
          icon="pi pi-user"
          shape="circle"
          image={option?.avatar ? option?.avatar : ""}
          label={
            option?.name
              ? ""
              : option?.lastname
                ? Array.from(option?.lastname)[0] +
                  (option?.firstname ? Array.from(option?.firstname)[0] : "")
                : option?.name
                  ? Array.from(option?.name)[0]
                  : ""
          }
          size="normal"
        ></Avatar>
        <div>
          {option?.name
            ? option?.name
            : option.firstname + " " + option.lastname}
        </div>
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} />

      <Dialog
        header="Modifier le membre associé"
        visible={active}
        style={{ width: "50vw" }}
        breakpoints={{ "960px": "75vw", "641px": "100vw" }}
        onHide={() => setActive(false)}
        footer={footerContent}
      >
        <div className="card flex justify-content-center">
          <ListBox
            filter
            value={selectedMember}
            onChange={(e) => setSelectedMember(e.value)}
            options={selectableMembers}
            optionLabel="name"
            itemTemplate={memberTemplate}
            className="w-full"
          />
        </div>
      </Dialog>
    </>
  );
});

export default UpdateTeacherMemberModal;
